import React, { useEffect, useState } from "react";
import { Button, Select, Input, Form, message } from "antd";
import { ConversationInquiryType } from "common/enums/ConversationInquiryType.enum";
import { useNavigate, useParams } from "react-router-dom";
import { axiosInstance } from "config/axiosConfig";
import { API_ENDPOINTS } from "config/apiConfig";
import { useCommonContext } from "contexts/CommonContext";
import { hideLoading, showLoading } from "reducers/loadingSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import CustomBreadCrumb from "components/CustomBreadCrumb";

const ConversationInquiryAdminPage = () => {
  const { setHeaderTitle } = useCommonContext();
  const [form] = Form.useForm();
  const dispatch = useDispatch<AppDispatch>();
  const [errors] = useState<{ [key: string]: boolean }>({});
  const [companyDetail, setCompanyDetail] = useState<any>({});
  const navigate = useNavigate();
  const { companyCode } = useParams();
  // Handle form submission
  const handleSubmit = async (values: any) => {
    if (!companyDetail.id) return;
    dispatch(showLoading());
    await axiosInstance
      .post(API_ENDPOINTS.CONVERSATION_INQUIRY, {
        ...values,
        company_id: companyDetail.id,
      })
      .then((res) => {
        if (res.status === 200) {
          dispatch(hideLoading());
          message.success("あなたのリクエストが作成されました。");
          navigate(
            `/admin/company-management/${res.data.data.id}/view-request`,
          );
        }
      });
  };

  const fetchCompanyDetail = async () => {
    await axiosInstance
      .get(API_ENDPOINTS.COMPANY_INFO + "/" + companyCode)
      .then((res) => {
        setCompanyDetail(res.data.data);
      });
  };

  useEffect(() => {
    fetchCompanyDetail();
    setHeaderTitle("新規お問い合わせ");
  }, []);

  return (
    <div className="self-stretch min-h-[924px] p-[20px] pb-[100px] md:p-10 bg-white inline-flex flex-col justify-start items-start gap-[30px] md:gap-12 overflow-hidden">
      <CustomBreadCrumb
        items={[
          {
            label: "ユーザー管理",
            link: "/admin/company-management",
          },
          {
            label: `ユーザー詳細（ID: #${companyCode}）`,
            link: `/admin/company-management/${companyCode}/view`,
          },
          {
            label: "新規お問い合わせ",
            isCurrent: true,
          },
        ]}
      />
      <div className="self-stretch flex flex-col justify-start items-start gap-[30px] md:gap-10">
        <div className="self-stretch flex flex-col justify-start items-start gap-[20px] md:gap-7">
          <div
            data-プロパティ1="base"
            className="self-stretch py-2.5 border-b border-neutral-border inline-flex justify-start items-center gap-3.5"
          >
            <div className="w-1 self-stretch bg-primary rounded-sm" />
            <div className="flex-1 flex justify-start items-center gap-2.5">
              <div className="flex justify-start items-center gap-[5px]">
                <div className="justify-start text-primary text-[18px] md:text-xl font-bold leading-9">
                  新規お問い合わせ
                </div>
              </div>
            </div>

            {/*section*/}
          </div>
          {/* Contact Form */}
          <div className="w-full max-w-4xl">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              className="bg-white flex flex-col gap-3.5"
            >
              {/* お問い合わせ種別 */}
              <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                <div className="w-44 flex justify-start items-center gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    お問い合わせ先
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <div className="flex h-[60px] px-[15px] flex-col justify-center items-start gap-[10px] self-stretch rounded-md border border-[var(--line-color,#D5D5D5)] bg-[var(--wh,#FFF)]">
                    {companyDetail.company_name}（ID: #{companyDetail.code}）
                  </div>
                </div>
              </div>

              {/* お問い合わせ種別 */}
              <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                <div className="w-44 flex justify-start items-center gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    お問い合わせ種別
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <Form.Item
                    name="type"
                    className="!mb-0"
                    rules={[
                      {
                        required: true,
                        message: "",
                      },
                    ]}
                  >
                    <Select
                      placeholder="お問い合わせ種別を選択する"
                      size="large"
                      status={errors.inquiry_type ? "error" : undefined}
                      className="w-full [&_.ant-select-selector]:!h-12"
                      options={ConversationInquiryType}
                    ></Select>
                  </Form.Item>
                </div>
              </div>

              {/* 件名 */}
              <div className="self-stretch md:inline-flex justify-start items-center gap-5">
                <div className="w-44 flex justify-start items-center gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    件名
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <Form.Item
                    name="subject"
                    className="!mb-0"
                    rules={[{ required: true, message: "" }]}
                  >
                    <Input
                      placeholder="例：出品ID「#00123」の商品について"
                      size="large"
                      status={errors.subject ? "error" : undefined}
                      className="!h-12"
                    />
                  </Form.Item>
                </div>
              </div>

              {/* 内容 */}
              <div className="self-stretch md:inline-flex justify-start items-start gap-5">
                <div className="w-44 py-3 flex justify-start items-start gap-2.5">
                  <div className="justify-start text-black text-[15px] md:text-base font-medium leading-relaxed">
                    メールアドレス
                  </div>
                </div>
                <div className="w-full md:w-auto flex-1">
                  <Form.Item
                    name="content"
                    className="!mb-0"
                    rules={[{ required: true, message: "" }]}
                  >
                    <Input.TextArea
                      placeholder="例：こんにちは。運営事務局です。売り手企業から取引チャットの返事がこないとお問い合わせがありました。3日以内に返事がなければ強制的にキャンセル処理をさせていただきます。"
                      rows={6}
                      status={errors.content ? "error" : undefined}
                      className="text-base"
                    />
                  </Form.Item>
                </div>
              </div>

              {/* 送信ボタン */}
              <div className="self-stretch flex justify-start items-start mt-6">
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  className="w-80 min-h-14 px-5 py-2.5 bg-primary rounded-md !text-white !font-bold !text-[16px] md:!text-lg hover:!bg-primary/90"
                >
                  送信する
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationInquiryAdminPage;
